import type { OrgFormValues } from '@/config/organizations';
import { useEditOrganization } from '@/config/organizations/useEditOrganization';
import { mainIconSize } from '@/constants';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { PencilLine } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import type { FC, SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyles from '../../../styles/index.module.css';

import { BasicInfoSection } from './BasicInfoSection';
import { ContactFields } from './ContactFields';
import styles from './styles.module.css';
import { SubscriptionSection } from './SubscriptionSection';

interface OrganizationDetailsEditProps {
  initialValues: OrgFormValues;
  /** The viewer may never edit, set from `!isGlobalSuperAdmin()`. `PUT /orgs/{org_id}` is restricted to platform super
   *  admins (`user.is_super_admin`, independent of org roles), so anyone else would be rejected on save. */
  isReadOnly?: boolean;
  onCancel?: () => void;
  onSave?: (values: OrgFormValues) => void;
  orgId: string;
}

export const OrganizationDetailsEdit: FC<OrganizationDetailsEditProps> = ({
  initialValues,
  isReadOnly = false,
  onCancel,
  onSave,
  orgId,
}) => {
  const { t } = useTranslation('orgs');
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);

  const onSaved = useCallback(
    (values: OrgFormValues) => {
      setIsEditing(false);
      onSave?.(values);
    },
    [onSave],
  );

  const { form } = useEditOrganization({ orgId, initialValues, onSave: onSaved });

  // Even a permitted user lands on the view page first and opts into editing (POR-428).
  const isViewing = isReadOnly || !isEditing;

  // Stored data can fail the form schema on a field the user never touches (a null alias, a phone the
  // backend accepts but this schema doesn't). Untouched errors stay hidden by default, which would
  // disable Save with nothing on screen explaining why — so surface them once editing starts.
  const showErrors = !isViewing;

  // Gated on isViewing, not isEditing: losing super-admin mid-edit flips isReadOnly and relabels this
  // to Back, and keying on isEditing would leave that Back button doing nothing.
  const onCancelHandler = useCallback(() => {
    if (!isViewing) {
      form.reset();
      setIsEditing(false);
      return;
    }
    setIsEditing(false);
    if (onCancel) {
      onCancel();
    } else {
      navigate({ to: '/organizations' });
    }
  }, [form, isViewing, navigate, onCancel]);

  const onSubmitHandler = async (e: SubmitEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    // Hiding Save is presentation, not a guard — a submit event can still reach here without it.
    if (isViewing) return;

    await form.handleSubmit();
  };

  return (
    <form className={styles.editFormContainer} onSubmit={onSubmitHandler}>
      <Stack alignItems="center" gap={4} justifyContent="space-between" orientation="horizontal">
        <form.Subscribe selector={s => s.values.name}>{name => <Text as="h1">{name}</Text>}</form.Subscribe>

        {!isReadOnly && !isEditing && (
          <Tooltip
            content={t('edit_org_edit_tooltip')}
            placement="left"
            trigger={
              <Button
                aria-label={t('edit_org_edit_tooltip')}
                data-testid="edit-org-btn"
                onPress={() => setIsEditing(true)}
                size="md"
                type="button"
                visual="unstyled"
              >
                <PencilLine size={mainIconSize} />
              </Button>
            }
          />
        )}
      </Stack>

      {isReadOnly && (
        <Text as="p" data-testid="org-read-only-notice">
          {t('edit_org_read_only')}
        </Text>
      )}

      <Stack className={clsx(globalStyles.scrollbars, styles.editFormControlsContainer)} gap={4} justifyContent="start">
        {/* Alias lock (POR-396) is frontend-only — PUT /orgs/{org_id} still accepts an alias change. */}
        <BasicInfoSection form={form} isReadOnly={isViewing} isAliasEditable={false} showErrors={showErrors} />

        <Grid columns={2} fullWidth gap={8}>
          <ContactFields form={form} isReadOnly={isViewing} section="primaryContact" showErrors={showErrors} />
          <ContactFields form={form} isReadOnly={isViewing} section="itContact" showErrors={showErrors} />
        </Grid>

        <SubscriptionSection
          form={form}
          isReadOnly={isViewing}
          originalExpiration={initialValues.subscription?.expiration}
          showErrors={showErrors}
        />
      </Stack>

      <form.Subscribe selector={s => [s.isDirty, s.canSubmit, s.isSubmitting]}>
        {([isDirty, canSubmit, isSubmitting]) => (
          <Grid className={styles.editFormFooter} columns={2} fullWidth gap={8}>
            <Button data-testid="cancel-btn" onPress={onCancelHandler} type="button" visual="default">
              {isViewing ? t('edit_org_back') : t('create_org_cancel')}
            </Button>

            {!isViewing && (
              <Button data-testid="save-btn" isDisabled={!isDirty || !canSubmit} type="submit" visual="primary">
                {isSubmitting ? <>{t('edit_org_saving')}&hellip;</> : t('edit_org_save_all')}
              </Button>
            )}
          </Grid>
        )}
      </form.Subscribe>
    </form>
  );
};
